import { api, config } from '@detox/actions';
import { ORDER } from '../../constants';
import isEmpty from '../../helpers/isEmpty';
import omitUndefined from '../../helpers/omitUndefined';
import is5GVasAddon from '../../helpers/is-5g-addon';
import translate from '../../hooks/useTranslation';

const { isDeviceCare } = api.mcss.helpersCart;
const { helpersAddons, helpers } = api.mcss;
const { isDataXProductSpec, isNewSelectedAddon } = helpersAddons;

const compare = (a, b) => {
  if (a > b) return 1;
  if (a === b) return 0;
  return -1;
};
const updateActionAddons = (actions, type) =>
  actions.map(action => {
    if (action.type === type) {
      return {
        ...action,
        allowed: true
      };
    }
    return action;
  });

// compare tier of addons
// - compare by salesRanking & title
const compareRankingAddons = (addonA = {}, addonB = {}) => {
  if (addonA.salesRanking && addonB.salesRanking) {
    return compare(
      parseInt(addonA.salesRanking),
      parseInt(addonB.salesRanking)
    );
  }
  return compare(
    addonA?.productSpecPricing?.childPricingSchema?.description,
    addonB?.productSpecPricing?.childPricingSchema?.description
  );
};

// updateMobileSwop to update button behavious including disabled/ title
// - Always show ADD button at the first time
// - Disable button when reach to addon's maximum
// - Only show REMOVE button after customer select add button
// - Customer can able to select the existing mobile swop addons if it not each to maximum
const updateMobileSwop = mobileSwopAddon => {
  const {
    selected,
    action,
    status,
    allowedActions,
    selectedMatchesMaxQuantity
  } = mobileSwopAddon;
  if (selected && action === 'UP' && status === 'AC') {
    return {
      ...mobileSwopAddon,
      selected: false,
      disabled: selectedMatchesMaxQuantity,
      // Update 'ADD' button
      allowedActions: updateActionAddons(allowedActions, 'AD')
    };
  }
  return mobileSwopAddon;
};

// Should Hide DATA X if
//    + it is lower exisiting dataX
//    + it is the existing dataX
//    + if it is XO Plus Platinum Prices
const shouldHideDataX = (addon, dependenceData) => {
  const { orderType, selectedDataX, selectedPlan } = dependenceData;
  const isXOPlusPlatinum =
    selectedPlan.planName === translate().t('XO_PLUS_PLATINUM_PLAN');
  const hideLowerExistingDataX =
    selectedDataX && compareRankingAddons(addon, selectedDataX) < 0;
  const hideExistingDataX =
    compareRankingAddons(addon, selectedDataX) === 0 &&
    !isNewSelectedAddon(addon);
  return (
    isXOPlusPlatinum ||
    (orderType === ORDER.TYPE.RECON &&
      (hideLowerExistingDataX || hideExistingDataX))
  );
};

const updateDataX = (dataX, dependenceData = {}) => {
  const { addonInContract, orderType, selectedDataX } = dependenceData;
  if (shouldHideDataX(dataX, dependenceData)) return undefined;
  const canUpgrade =
    orderType === ORDER.TYPE.RECON &&
    selectedDataX &&
    compareRankingAddons(dataX, selectedDataX) > 0;
  return {
    ...dataX,
    allowedActions: canUpgrade
      ? updateActionAddons(dataX.allowedActions, 'UP')
      : dataX.allowedActions,
    addonInContract,
    removedAddon: selectedDataX
  };
};

const getDependenceData = (availableAddons, selectedAddonsBo, options) => {
  const addonInContracts = Object.values({
    ...selectedAddonsBo,
    ...availableAddons
  }).filter(
    addon =>
      isDataXProductSpec(addon.productSpecContainmentID) &&
      helpersAddons.isAddonInContract(addon)
  );

  const selectedDataXes = Object.values({
    ...selectedAddonsBo,
    ...availableAddons
  }).filter(
    addon =>
      isDataXProductSpec(addon.productSpecContainmentID) && addon.selected
  );

  // selectedDataX including existing dataX & new selected dataX
  const selectedDataX =
    selectedDataXes.find(item => item.productTitle) || selectedDataXes?.[0];

  const addonInContract =
    addonInContracts.find(item => item.productTitle) || addonInContracts?.[0];
  return {
    addonInContract,
    hasAddonInContract: !isEmpty(addonInContract),
    selectedDataX,
    ...options
  };
};

// updateAddons will update addons from client
//  + Mobile Swop update - done
//  + DataX  - done
//  + Spotify - done - findAtomicAddons
//  + 5G - done - hide5GVas
// Why does we need to handle it from our side?
//   API doesn't handle correctly
/**
 * updateAddons will update addons from client
 * @param availableAddons
 * @param selectedAddonsBo
 * @param options
 * @param options.orderType {string} recontract order indicator
 * @return {*}
 */
const updateAddons = (availableAddons = {}, selectedAddonsBo, options = {}) => {
  const dependenceData = getDependenceData(
    availableAddons,
    selectedAddonsBo,
    options
  );

  const updatedAddons = Object.entries(availableAddons).reduce(
    (acc, [key, addon]) => {
      const { productSpecContainmentID } = addon;

      // Check if addon is SimOnly Addon
      if (addon?.simOnlyAddon) {
        return {
          ...acc
        };
      }
      // Check if addon is mobile swop
      if (isDeviceCare({ productSpecContainmentID })) {
        const updatedMobileSwopAddon = updateMobileSwop(addon);
        return {
          ...acc,
          [key]: updatedMobileSwopAddon
        };
      }
      if (isDataXProductSpec(productSpecContainmentID)) {
        const updatedDataX = updateDataX(addon, dependenceData);
        return {
          ...acc,
          [key]: updatedDataX
        };
      }
      //Include 5G Vas Addon for all customers.
      if (is5GVasAddon(addon).length) {
        return {
          ...acc
        };
      }
      //Hide addon if customer has existing addon under the same group.
      if (
        isGroupAddon(addon) &&
        hasExistingAddonsInGroup(addon, availableAddons)
      ) {
        return {
          ...acc
        };
      }

      return {
        ...acc,
        [key]: updateGenericAddon(addon, dependenceData, options)
      };
    },
    {}
  );
  return omitUndefined(updatedAddons);
};

/**
 * Indicator to verify if addon is a group addon.
 *
 * @param {*} addon - addon
 */
export const isGroupAddon = addon => {
  return addon.groupName;
};

/**
 * Indicator to search customer existing addon within the same group.
 *
 * @param {*} addon - current addon
 * @param {*} availableAddons - list of available addons
 */
export const hasExistingAddonsInGroup = (addon, availableAddons) => {
  let hasExistingAddon = false;
  Object.entries(availableAddons).forEach(([key, cur]) => {
    const { groupName, selected, action } = cur;
    // search existing addon under same group
    if (
      addon.groupName === groupName &&
      selected &&
      (!action || action === 'UP')
    ) {
      hasExistingAddon = true;
    }
  });
  return hasExistingAddon;
};

// Addons logic is spread out in this helper file and AddonCatalogue.
// TODO: move all logic to this helper. Keep AddonCatalogue pure UI.
function updateGenericAddon(addon, dependenceData, options = {}) {
  const isDisabled = addon.selectedMatchesMaxQuantity;
  // Hide user's existing addons for recontract flow.
  if (isUserHasExistingAddon(addon)) return null;
  return {
    ...addon,
    disabled: isDisabled
  };
}

function isUserHasExistingAddon(addon) {
  const canAdd = helpersAddons.canAddBillingOffer(addon);
  const canUpgrade = shopAddonHelpers.canUpgradeBillingOffer(addon);
  const canRemove = helpersAddons.canRemoveBillingOffer(addon);
  const isNewlyAdded = shopAddonHelpers.isNewlyAddedAddon(addon);
  const isAspire = shopAddonHelpers.isAspireAddon(addon);

  // e.g. CallerID,
  const userHasExistingAspireAddon =
    isAspire && !isNewlyAdded && (canRemove || canUpgrade);

  if (isAspire) {
    return userHasExistingAspireAddon;
  } else {
    return !isNewlyAdded && canRemove;
  }
}

const shopAddonHelpers = {
  canUpgradeBillingOffer: addon => {
    if (addon.allowedActions) {
      const upgradeAction = addon.allowedActions.find(
        action => action.type === 'UP'
      );
      return Boolean(upgradeAction?.allowed);
    } else {
      return false;
    }
  },
  isNewlyAddedAddon: addon => {
    return addon.action === 'AD';
  },
  isAspireAddon: addon => {
    const aspireAddonProductSpecContainmentIds = [
      config.callerIDProductSpecContainmentId
    ];
    const addonProductSpecContainmentId = addon.productSpecContainmentID || '';
    return aspireAddonProductSpecContainmentIds.includes(
      addonProductSpecContainmentId
    );
  }
};

export { updateAddons, compareRankingAddons, isUserHasExistingAddon };
